import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { message, Button as AntButton } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ArrowLeftOutlined, DownloadOutlined, MailOutlined } from "@ant-design/icons";
import { PageShell, PageHeader, Panel, DataTable } from "../components/Page";
import {
  fetchSaleInvoices,
  downloadInvoicePdf,
  sendInvoiceEmail,
  type SaleInvoiceRow,
} from "../api/auth";

const bdt = (n: number) => `৳${Number(n || 0).toLocaleString()}`;

function handleDownload(row: SaleInvoiceRow) {
  downloadInvoicePdf(row.id)
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${row.invoiceNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    })
    .catch((err) => {
      message.error((err as { message?: string })?.message ?? "Failed to download invoice PDF");
    });
}

function handleEmail(row: SaleInvoiceRow) {
  sendInvoiceEmail(row.id)
    .then(() => message.success(`Invoice emailed to ${row.buyerEmail || "customer"}`))
    .catch((err) => {
      message.error((err as { message?: string })?.message ?? "Failed to send invoice email");
    });
}

const columns: ColumnsType<SaleInvoiceRow> = [
  {
    title: "Invoice #",
    dataIndex: "invoiceNo",
    key: "invoiceNo",
    render: (invoiceNo: string) => <span className="font-mono text-xs text-stone-700">{invoiceNo}</span>,
  },
  {
    title: "Vehicle",
    key: "vehicle",
    render: (_text, row) => (
      <div>
        <div className="font-medium text-stone-800">{row.vehicleTitle}</div>
        <div className="text-xs text-stone-500 font-mono">{row.vehiclePublicId}</div>
      </div>
    ),
  },
  {
    title: "Buyer",
    key: "buyer",
    render: (_text, row) => (
      <div>
        <div className="font-medium text-stone-800">{row.buyerName}</div>
        <div className="text-xs text-stone-500">{row.buyerEmail || "—"}</div>
      </div>
    ),
  },
  {
    title: "Sale Date",
    dataIndex: "saleDate",
    key: "saleDate",
    render: (saleDate: string) => <span className="text-stone-700">{saleDate || "—"}</span>,
  },
  { title: "Total (BDT)", key: "total", render: (_t, row) => <span className="font-mono text-stone-800">{bdt(row.total)}</span> },
  { title: "Paid", key: "paid", render: (_t, row) => <span className="font-mono text-stone-600">{bdt(row.paid)}</span> },
  { title: "Balance", key: "balance", render: (_t, row) => <span className="font-mono text-stone-800">{bdt(row.balance)}</span> },
  {
    title: "Status",
    key: "status",
    render: (_text, row) => (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
          row.balance <= 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
        }`}
      >
        {row.balance <= 0 ? "Paid in full" : "Balance due"}
      </span>
    ),
  },
  {
    title: "Actions",
    key: "actions",
    render: (_text, row) => (
      <div className="flex gap-2">
        <AntButton
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(row)}
          title="Download PDF"
        />
        <AntButton
          type="text"
          size="small"
          icon={<MailOutlined />}
          onClick={() => handleEmail(row)}
          title="Email Invoice"
        />
      </div>
    ),
  },
];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useQuery({
    queryKey: ["sale-invoices", page],
    queryFn: () => fetchSaleInvoices(page, 20),
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);

  if (error) {
    return (
      <PageShell>
        <PageHeader
          eyebrow="Transactions"
          title="Invoices"
          description="Browse and manage generated sale invoices."
          actions={
            <AntButton onClick={() => navigate("/sales/manage")} icon={<ArrowLeftOutlined />}>
              Back
            </AntButton>
          }
        />
        <Panel flush title="Invoices">
          <div className="p-4 border border-stone-200 bg-stone-50 rounded-lg">
            <div className="text-stone-700 font-medium mb-2">Failed to load invoices</div>
            <div className="text-sm text-stone-600">
              {(error as { message?: string })?.message ?? "Unknown error"}
            </div>
          </div>
        </Panel>
      </PageShell>
    );
  }

  if (isLoading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-petrol"></div>
        </div>
      </PageShell>
    );
  }

  if (rows.length === 0) {
    return (
      <PageShell>
        <PageHeader
          eyebrow="Transactions"
          title="Invoices"
          description="Browse and manage generated sale invoices."
          actions={
            <AntButton onClick={() => navigate("/sales/manage")} icon={<ArrowLeftOutlined />}>
              Back
            </AntButton>
          }
        />
        <Panel flush title="Invoices">
          <div className="p-12 flex flex-col items-center justify-center text-center border border-stone-200 bg-stone-50 rounded-lg">
            <div className="text-stone-400 mb-2">No invoices yet</div>
            <div className="text-sm text-stone-500">Generate your first invoice from a sale.</div>
          </div>
        </Panel>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        eyebrow="Transactions"
        title="Invoices"
        description="Browse and manage generated sale invoices."
        actions={
          <AntButton onClick={() => navigate("/sales/manage")} icon={<ArrowLeftOutlined />}>
            Back
          </AntButton>
        }
      />

      <Panel flush title="Invoices">
        <DataTable<SaleInvoiceRow>
          columns={columns}
          rows={rows}
          rowKey="id"
          total={data?.total ?? rows.length}
          page={page}
          pageSize={20}
          onPageChange={setPage}
          loading={isLoading}
          emptyText="No invoices found"
        />
      </Panel>
    </PageShell>
  );
}